import React, { useCallback, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Col, Row, Form, Divider, Button, Alert } from "antd";

import ClientFile from "./upload";
import DocumentTypes from "./documentTypes";
import InfoLabel from "../../app/generic/infoLabel";
import InputItem from "../../app/generic/inputItem";
import CheckboxItem from "../../app/generic/checkboxItem";
import TextAreaItem from "../../app/generic/textAreaItem";
import { error, success } from "../../../modules/messages";
import { handleError } from "../../../modules/errorLogger";
import RadioGroupItem from "../../app/generic/radioGroupItem";
import { checkHttpStatus, postForm } from "../../../modules/httpFunctions";
import DateItem, { getDateFromDatePicker } from "../../app/generic/dateItem";

function Edit({ form, document }) {
  const intl = useIntl();
  const { getFieldDecorator } = form;
  const formItemLayout = {
    colon: false,
  };

  const [showClientFile, setShowClientFile] = useState();
  const [clientFiles, setClientFiles] = useState([]);

  const [files, setFiles] = useState([]);
  const [showFile, setShowFile] = useState(true);

  const [hasValidTo, setHasValidTo] = useState();

  const [loading, setLoading] = useState();
  const [notFoundClients, setNotFoundClients] = useState();
  const handleSubmit = useCallback(
    (e) => {
      e.preventDefault();
      form.validateFields((err, values) => {
        setNotFoundClients();
        if (!err) {
          const link = values.fileType === 2;
          const manyClients = values.type === 2;
          const clientFile = clientFiles.find((t) => true);
          const document = files.find((t) => true);
          if (link || document) {
            if (!manyClients || clientFile) {
              setLoading(true);
              const newDocument = {
                ...values,
                validFrom: getDateFromDatePicker(values.validFrom),
                validTo: getDateFromDatePicker(values.validTo) ?? "",
                blockOnReject: values.blockOnReject === 1,
                description: values.description || "",
                autoResult: values.autoResult || 0,
                emails: values.emails || "",
                link: link,
              };
              const formData = new FormData();
              !link && formData.append("document", document?.originFileObj);
              manyClients &&
                formData.append("clientFile", clientFile?.originFileObj);
              Object.keys(newDocument).forEach((k) =>
                formData.append(k, newDocument[k])
              );
              postForm("document", formData, true, undefined, 360000)
                .then((r) => {
                  if (checkHttpStatus(r)) {
                    setFiles([]);
                    setClientFiles([]);
                    form.resetFields();
                    success(
                      intl.formatMessage({
                        id: "document.create.success",
                        defaultMessage: "Dokument byl úspěšně vytvořen.",
                      })
                    );
                  } else if (r.response.status === 400) {
                    error(
                      intl.formatMessage({
                        id: "document.create.badData",
                        defaultMessage: "Vyplněné data nejsou korektní.",
                      })
                    );
                  } else if (r.response.status === 402) {
                    error(
                      intl.formatMessage({
                        id: "document.create.noClients",
                        defaultMessage:
                          "Ze souboru se nepodařilo načíst žádné klienty",
                      })
                    );
                  } else if (r.response.status === 412) {
                    setNotFoundClients(r.response.data?.data);
                    error(
                      intl.formatMessage({
                        id: "document.create.notFoundClients",
                        defaultMessage:
                          "Někteří zadaní klienti nebyli nalezeni.",
                      })
                    );
                  } else if (r.response.status === 424) {
                    error(
                      intl.formatMessage({
                        id: "document.create.fileError",
                        defaultMessage:
                          "Nepodařilo se odeslat soubor, zkuste pro prosím znovu.",
                      })
                    );
                  } else {
                    error(
                      intl.formatMessage({
                        id: "document.create.error",
                        defaultMessage: "Při vytváření dokumentu nastala chyba",
                      })
                    );
                  }
                  setLoading();
                })
                .catch((err) => {
                  handleError(err);
                  error(
                    intl.formatMessage({
                      id: "document.create.error",
                      defaultMessage: "Při vytváření dokumentu nastala chyba",
                    })
                  );
                  setLoading();
                });
            } else {
              error(
                intl.formatMessage({
                  id: "document.noClientFile",
                  defaultMessage: "Vložte prosím soubor s výčtem klientů",
                })
              );
            }
          } else {
            error(
              intl.formatMessage({
                id: "document.noDocument",
                defaultMessage: "Vložte prosím dokument",
              })
            );
          }
        }
      });
    },
    [form, intl, files, clientFiles]
  );

  return (
    <Row type="flex" justify="center">
      <Col xl={12} xs={24}>
        <Form {...formItemLayout} onSubmit={handleSubmit}>
          <RadioGroupItem
            button
            initialValue={1}
            fieldName="type"
            formItemLayout={formItemLayout}
            getFieldDecorator={getFieldDecorator}
            onChange={({ target: { value } }) => setShowClientFile(value === 2)}
            labels={[
              intl.formatMessage({
                id: "document.current",
                defaultMessage: "Aktuální klient",
              }),
              <InfoLabel
                label={intl.formatMessage({
                  id: "document.clients",
                  defaultMessage: "Výčet klientů",
                })}
                info={intl.formatMessage({
                  id: "document.clients.info",
                  defaultMessage:
                    "vložte prosím níže soubor s jedinečnými identifikátory klienta (IČO, DIČ, EVO Contract, ZAccount) oddělenými středníkem, čárkou nebo enterem",
                })}
              />,
            ]}
          />
          {showClientFile && (
            <ClientFile
              currentFiles={clientFiles}
              onSetCurrentFiles={setClientFiles}
            />
          )}
          <Divider />
          <RadioGroupItem
            button
            initialValue={1}
            fieldName="fileType"
            formItemLayout={formItemLayout}
            getFieldDecorator={getFieldDecorator}
            onChange={({ target: { value } }) => setShowFile(value === 1)}
            labels={[
              intl.formatMessage({
                id: "document.file",
                defaultMessage: "Soubor",
              }),
              intl.formatMessage({
                id: "document.url",
                defaultMessage: "Odkaz",
              }),
            ]}
          />
          {showFile ? (
            <ClientFile all currentFiles={files} onSetCurrentFiles={setFiles} />
          ) : (
            <InputItem
              url
              required
              fieldName="url"
              label={intl.formatMessage({
                id: "document.url",
                defaultMessage: "Odkaz",
              })}
              formItemLayout={formItemLayout}
              getFieldDecorator={getFieldDecorator}
            />
          )}
          <Divider />
          <InputItem
            required
            fieldName="name"
            label={intl.formatMessage({
              id: "document.name",
              defaultMessage: "Název dokumentu pro klienta",
            })}
            formItemLayout={formItemLayout}
            getFieldDecorator={getFieldDecorator}
          />
          <TextAreaItem
            allowClear
            label={intl.formatMessage({
              id: "document.desc",
              defaultMessage: "Popis dokumentu",
            })}
            fieldName="description"
            getFieldDecorator={getFieldDecorator}
            autoSize={{ minRows: 3, maxRows: 6 }}
          />
          <DocumentTypes
            formItemLayout={formItemLayout}
            getFieldDecorator={getFieldDecorator}
          />
          {/* <DateRange
            form={form}
            onSetHasValidTo={setHasValidTo}
            formItemLayout={formItemLayout}
            getFieldDecorator={getFieldDecorator}
          /> */}
          <DateItem
            required
            fromYear
            fieldName="validFrom"
            formItemLayout={formItemLayout}
            getFieldDecorator={getFieldDecorator}
            label={intl.formatMessage({
              id: "document.validFrom",
              defaultMessage: "Platné od",
            })}
          />
          <DateItem
            fromToday
            fieldName="validTo"
            formItemLayout={formItemLayout}
            getFieldDecorator={getFieldDecorator}
            onChange={(date) => setHasValidTo(date ? true : false)}
            label={intl.formatMessage({
              id: "document.validTo",
              defaultMessage: "Platné do",
            })}
          />
          {hasValidTo && (
            <RadioGroupItem
              button
              initialValue={1}
              fieldName="autoResult"
              formItemLayout={formItemLayout}
              getFieldDecorator={getFieldDecorator}
              label={intl.formatMessage({
                id: "document.autoResult",
                defaultMessage: "Po uplynutí platnosti",
              })}
              labels={[
                intl.formatMessage({
                  id: "document.autoResult.confirm",
                  defaultMessage: "Automaticky schválit",
                }),
                intl.formatMessage({
                  id: "document.autoResult.reject",
                  defaultMessage: "Automaticky zamítnout",
                }),
              ]}
            />
          )}
          <RadioGroupItem
            button
            initialValue={2}
            fieldName="blockOnReject"
            formItemLayout={formItemLayout}
            getFieldDecorator={getFieldDecorator}
            label={intl.formatMessage({
              id: "document.blockOnReject",
              defaultMessage: "V případě zamítnutí zablokovat klienta",
            })}
            labels={[
              intl.formatMessage({
                id: "messages.yes",
                defaultMessage: "Ano",
              }),
              intl.formatMessage({
                id: "messages.no",
                defaultMessage: "Ne",
              }),
            ]}
          />
          <Divider />
          <InputItem
            multipleEmails
            fieldName="emails"
            autoComplete="email"
            formItemLayout={formItemLayout}
            getFieldDecorator={getFieldDecorator}
            label={
              <InfoLabel
                label={intl.formatMessage({
                  id: "document.email",
                  defaultMessage: "Email",
                })}
                info={intl.formatMessage({
                  id: "document.email.info",
                  defaultMessage:
                    "Je možné zadat více emailových adres oddělených středníkem.",
                })}
              />
            }
          />
          <Row type="flex">
            <Col span={8}>
              <CheckboxItem
                fieldName="notifyOnConfirm"
                label={intl.formatMessage({
                  id: "document.email.confirm",
                  defaultMessage: "Informovat při souhlasu",
                })}
                formItemLayout={formItemLayout}
                getFieldDecorator={getFieldDecorator}
              />
            </Col>
            <Col span={8}>
              <CheckboxItem
                fieldName="notifyOnReject"
                label={intl.formatMessage({
                  id: "document.email.reject",
                  defaultMessage: "Informovat při odmítnutí",
                })}
                formItemLayout={formItemLayout}
                getFieldDecorator={getFieldDecorator}
              />
            </Col>
          </Row>
          <Divider />
          {notFoundClients && (
            <>
              <Alert
                closable
                type="warning"
                message={
                  <FormattedMessage
                    id="document.clients.notFound"
                    defaultMessage="Nebyly nalezeni klienti: {clients}"
                    values={{ clients: notFoundClients.join(", ") }}
                  />
                }
              />
              <Divider />
            </>
          )}
          <Row type="flex" justify="end">
            <Col>
              <Button type="primary" htmlType="submit" loading={loading}>
                {intl.formatMessage({
                  id: "messages.save",
                  defaultMessage: "Uložit",
                })}
              </Button>
            </Col>
          </Row>
        </Form>
      </Col>
    </Row>
  );
}

export default Form.create()(Edit);
